import { ApolloError, useQuery } from "@apollo/client";
import { gql } from "generated-graphql";
import { DynamicFieldFragment } from "generated-graphql/graphql";

/**
 * Mirror of the compliance-engine `PrefillSource` union
 * (packages/encamp-compliance-engine `PrefillSource`). Keep in sync if a new
 * source is added there.
 */
export type PrefillSource = "wasteSourceCodeDescriptions" | "wasteStreamNames";

const PREFILL_SOURCES: readonly PrefillSource[] = [
  "wasteSourceCodeDescriptions",
  "wasteStreamNames",
];

function isPrefillSource(value: string | string[]): value is PrefillSource {
  return typeof value === "string" && PREFILL_SOURCES.some((source) => source === value);
}

/**
 * Reads `metadata.prefillSource` from a DynamicField's `metadata` (a JSON scalar,
 * typed as `any` in generated types — read directly, as elsewhere in DynamicField),
 * validating it against the known sources.
 */
export function readPrefillSource(
  metadata: DynamicFieldFragment["metadata"]
): PrefillSource | undefined {
  const source = metadata?.prefillSource;
  return isPrefillSource(source) ? source : undefined;
}

export type WasteStateFieldSuggestions = {
  descriptionOfFacilityProcesses?: string | null;
  hazardousWasteGeneration?: string | null;
};

const WASTE_STATE_FIELD_SUGGESTIONS = gql(`
  query WasteStateFieldSuggestions($facilityId: ID!) {
    wasteStateFieldSuggestions(facilityId: $facilityId) {
      descriptionOfFacilityProcesses
      hazardousWasteGeneration
    }
  }
`);

export type UsePrefillWasteStateFieldResult = {
  suggestions: WasteStateFieldSuggestions | null;
  loading: boolean;
  error: ApolloError | undefined;
};

/**
 * Eagerly fetches the waste-data pre-fill suggestions for a facility in a single
 * query so callers can decide *before* the user clicks whether there is anything
 * to pre-fill (and whether the user is even allowed to read the data). Pass
 * `skip` when there are no prefillable fields to avoid a needless request.
 *
 * Callers map each suggestion to the field(s) tagged with the matching
 * `prefillSource` metadata.
 */
export function usePrefillWasteStateField(
  facilityId: string | null | undefined,
  skip = false
): UsePrefillWasteStateFieldResult {
  const { data, loading, error } = useQuery(WASTE_STATE_FIELD_SUGGESTIONS, {
    variables: { facilityId: facilityId ?? "" },
    skip: skip || !facilityId,
    fetchPolicy: "network-only",
  });

  return {
    suggestions: data?.wasteStateFieldSuggestions ?? null,
    loading,
    error,
  };
}

/** Selects the suggestion string for a given prefill source. */
export function suggestionForSource(
  suggestions: WasteStateFieldSuggestions,
  source: PrefillSource
): string {
  const value =
    source === "wasteSourceCodeDescriptions"
      ? suggestions.descriptionOfFacilityProcesses
      : suggestions.hazardousWasteGeneration;
  return value ?? "";
}
